import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * Keeps the sold-out "Make an Enquiry" trigger in sync with the selected variant.
 *
 * The block (blocks/_ap-sold-out-enquiry.liquid) renders its own correct state on the
 * server, so first paint, `?variant=` deep links and the no-JS case are already right.
 * On every variant change the variant picker re-fetches the product section and emits it
 * as `event.detail.data.html`; we morph this element from its freshly rendered
 * counterpart in that HTML. All eligibility, sold-out and display-label logic therefore
 * stays server-side — this script never recomputes any of it, it only adopts the
 * server's answer. Same pattern as ap-sold-out-similar-styles.js and local-pickup.js.
 *
 * Showing and hiding the button is the `hidden` attribute on the TRIGGER that the morph
 * carries over; hiding the disabled add-to-cart button beside it is a :has() rule in the
 * block's stylesheet. Neither is done from here — there is no state in this class beyond
 * the last-known visibility, which exists only to debounce the impression event.
 *
 * Why the trigger and not this host: `morph()` runs with `childrenOnly: true`
 * (assets/morph.js), so the morph target's OWN attributes are never updated — only its
 * children are. Any per-variant state has to live inside the host, or it silently freezes
 * at its first-paint value. Same division as _ap-sold-out-similar-row.
 *
 * The click itself is handled by assets/ap-enquiry-trigger.js (shared with the Bride
 * enquiry block), which opens the drawer and hands it this trigger's data-context-*
 * attributes. The listener here is analytics only.
 *
 * Analytics: dispatches `ap:sold-out-enquiry-shown` on each hidden -> visible transition
 * (the customer just selected a size we cannot sell) and `ap:sold-out-enquiry-click` when
 * the button is pressed. ap-variant-analytics.js translates both into
 * `ap_sold_out_enquiry_*` Web Pixels events, pairing them with its `ap_size_selected`
 * unmet-demand signal and the `ap_similar_styles_*` recovery path.
 */
class ApSoldOutEnquiry extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {boolean} */
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#listeners.signal,
    });
    this.addEventListener('click', this.#onClick, { signal: this.#listeners.signal });

    // A ?variant= deep link to a sold-out variant renders the button visible on first
    // paint — an impression no variant:update will ever report.
    this.#visible = this.#isVisible();
    if (this.#visible) this.#dispatch('ap:sold-out-enquiry-shown');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listeners?.abort();
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    const blockId = this.dataset.blockId;
    if (!html || !blockId) return;

    // Find this block's re-rendered counterpart for the newly selected variant.
    const next = html.querySelector(`ap-sold-out-enquiry[data-block-id="${CSS.escape(blockId)}"]`);
    if (next) morph(this, next);

    // Impression = each hidden -> visible transition, read after the morph so the payload
    // reflects the size that was just selected.
    const visible = this.#isVisible();
    if (visible && !this.#visible) this.#dispatch('ap:sold-out-enquiry-shown');
    this.#visible = visible;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-ap-enquiry-trigger]')) return;
    this.#dispatch('ap:sold-out-enquiry-click');
  };

  /**
   * The trigger — not this host — is the source of truth for every per-variant value.
   * `morph()` runs with `childrenOnly: true` (assets/morph.js), so a morph target's own
   * attributes are never updated; only its children are. Reading state off the host would
   * mean reading whatever the first paint happened to render, forever.
   * @returns {HTMLAnchorElement | HTMLButtonElement | null}
   */
  #trigger() {
    return this.querySelector('[data-ap-enquiry-trigger]');
  }

  #isVisible() {
    const trigger = this.#trigger();
    return !!trigger && !trigger.hasAttribute('hidden');
  }

  /** @param {string} name */
  #dispatch(name) {
    const trigger = this.#trigger();
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          productUrl: this.dataset.productUrl,
          variantId: trigger?.dataset.variantId,
          sizeLabel: trigger?.dataset.contextSize,
          colourLabel: trigger?.dataset.contextColour,
        },
      })
    );
  }
}

if (!customElements.get('ap-sold-out-enquiry')) {
  customElements.define('ap-sold-out-enquiry', ApSoldOutEnquiry);
}
